/**
 * ==========================================================================
 *  THE SAUNA DOOR, AND THE FACT THAT IT IS LOCKED
 * ==========================================================================
 *  Every other thing F reaches for in this level gives way. A door swings, a
 *  pane comes out of its frame, a kasa hands over a klapi, a hana fills a
 *  tuoppi. This one does nothing at all, and doing nothing at all is the
 *  point of it: the juhlasali's east wall has the saunatilat behind it, the
 *  sauna is taken, and the venue's answer to that is a man standing beside
 *  the handle rather than a sign taped over it.
 *
 *  ------------------------------------------------------------------------
 *   IT HAS NO COLLIDER, AND IT HAS NO STATE
 *  ------------------------------------------------------------------------
 *  `Door` is handed a collider and owns when it disappears; `Ikkuna` is
 *  handed one and owns the one step it goes for good. There is nothing to
 *  hand this one. The leaf is hung on `sali-ita`, which is a wall for the
 *  whole run whatever anybody presses, so the class is a focus point, a
 *  prompt, and an id to report - and `interact()` is empty on purpose.
 *
 *  That is also why `canInteract` never latches, and a locked door can be
 *  tried all morning: a runner who leans on it twice gets told twice. A `Door`
 *  goes on answering too - F reverses its leaf as often as anybody wants it
 *  reversed, see `Door` - but the two are unlatched for opposite reasons. That
 *  one keeps answering because the press always does something; this one keeps
 *  answering although the press never does. The pane and the chair, which can
 *  each only happen once, stop offering the moment they have.
 *
 *  ------------------------------------------------------------------------
 *   WHAT IT COSTS
 *  ------------------------------------------------------------------------
 *  Nothing, and that is a promise rather than an accident. It moves no
 *  collider, it stamps no timer, and it is outside `simulationFingerprint()`
 *  - so this door can be slid along the wall, relabelled or taken out again
 *  without retiring a single leaderboard time. See `SaunaSpec` and
 *  src/config/CLAUDE.md.
 *
 *  What it *is* worth is a step event: `RunSimulation` reports the press as
 *  `StepEvents.interacted`, and `Game` turns that into the man's line. The
 *  words are in src/config/dialogue.ts with every other line in the game,
 *  because a script that lived in the level would be a script nobody could
 *  find.
 *
 *  No three.js in here, like the rest of `src/world` - the leaf you can see
 *  is decor `World` draws, and this end goes into the Lambda bundle with the
 *  verifier.
 * ==========================================================================
 */

import { INTERACT } from '../config/gameplay';
import { boxCenter, type SaunaSpec } from './Level';
import { isLookingAt, type InteractContext, type Interactable } from './Interactable';
import type { Vec3 } from '../utils/math';

export class Sauna implements Interactable {
  readonly id: string;
  readonly prompt: string;

  private readonly center: Vec3;

  constructor(readonly spec: SaunaSpec) {
    this.id = spec.id;
    this.prompt = spec.label;
    this.center = boxCenter(spec.box);
  }

  focusPoint(): Vec3 {
    return this.center;
  }

  /**
   * A doorknob's reach and a doorknob's cone, because that is exactly what
   * this is. Unlatched, unlike every other interactable in the level: see the
   * header for why a locked door goes on answering.
   */
  canInteract(context: InteractContext): boolean {
    return isLookingAt(context, this.center, INTERACT.maxDistance, INTERACT.minLookDot);
  }

  /**
   * Nothing happens. The press is still worth reporting - `RunSimulation`
   * puts the id on the step and the man beside the door answers it - but
   * nothing in the world moves, which is what locked means.
   */
  interact(): void {}

  /** Nothing to put back either, for the same reason. */
  reset(): void {}
}
